import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import {finalize, forkJoin, Observable} from 'rxjs';
import { User } from '../../../../core/classes/user';
import { ApiKeyRequest } from '../../../../core/classes/apiKeyRequest';
import { GenApiKeyRequestStatus } from '../../../../generated/serverModels/GenApiKeyRequestStatus';
import { UserService } from '../../../../services/user.service';
import { ApiKeyRequestDetail, ApiKeyUserRequestRow } from './api-key-request-table.models';
import {
  ApiKeyRequestsTableComponent,
  DenyApiKeyRequestEvent,
} from './api-key-requests-table/api-key-requests-table.component';
import { MatDialog } from '@angular/material/dialog';
import { InformationModalComponent } from '../../../../shared/modals/information-modal/information-modal.component';
import { ApiKeyRequestDenyDialogComponent } from './api-key-request-deny-dialog/api-key-request-deny-dialog.component';
import { ApiKeyStatusDetail, ApiKeyStatusRow } from './api-key-status-table/api-key-status-table.models';
import { ApiKeyStatusTableComponent } from './api-key-status-table/api-key-status-table.component';

type ApiKeyAdminDataService = Pick<
  UserService,
  'getUsers' | 'getApiKeyRequests' | 'approveApiKeyRequest' | 'denyApiKeyRequest'
>;

@Component({
  selector: 'tgrid-api-key-management',
  imports: [MatTabsModule, ApiKeyRequestsTableComponent, ApiKeyStatusTableComponent],
  templateUrl: './api-key-management.component.html',
  styleUrl: './api-key-management.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiKeyManagementComponent {
  private readonly userService = inject(UserService) as ApiKeyAdminDataService;
  private readonly dialog = inject(MatDialog);

  protected readonly requestRows = signal<ApiKeyUserRequestRow[]>([]);
  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly statusRows = signal<ApiKeyStatusRow[]>([]);
  protected readonly statusLoading = signal(false);
  protected readonly statusErrorMessage = signal<string | null>(null);
  protected readonly processingRequestIds = signal(new Set<number>());
  protected readonly approvingRequestIds = computed(() =>
    Array.from(this.processingRequestIds())
  );
  // private readonly processingSet = computed(() => new Set(this.processingRequestIds()));

  constructor() {
    this.reloadTables();
  }

  protected approveRequest(requestId: number): void {
    this.executeDecision(
      requestId,
      this.userService.approveApiKeyRequest(requestId),
      'Approval Failed',
    );
    }

  protected openDenyDialog(event: DenyApiKeyRequestEvent): void {
    const dialogRef = this.dialog.open(ApiKeyRequestDenyDialogComponent, {
      width: '600px',
      data: {
        userName: event.userName,
      },
    });

    dialogRef.afterClosed().subscribe((deniedReason: string | undefined) => {
      const normalizedReason = deniedReason?.trim();

      if (!normalizedReason || this.isProcessing(event.requestId)) {
        return;
      }

      this.denyRequest(event.requestId, normalizedReason);
    });
  }

  private denyRequest(requestId: number, deniedReason: string): void {
    const normalizedReason = deniedReason.trim();
    if (!normalizedReason) {
      return;
    }

    this.executeDecision(
      requestId,
      this.userService.denyApiKeyRequest(requestId, normalizedReason),
      'Denial Failed',
    );
  }

  private openDecisionErrorDialog(error: unknown, title: string): void {
    this.dialog.open(InformationModalComponent, {
      width: '500px',
      data: {
        title,
        infoText: this.getUserFacingErrorMessage(error),
      },
    });
  }

  private getUserFacingErrorMessage(error: unknown): string {
    const apiError = error as { error?: { userMessage?: string } } | null;

    return apiError?.error?.userMessage
      ?? 'Unable to complete this request. Please try again.';
  }

  private reloadTables(): void {

    // every refresh currently makes two user fetches. is this preferred?
    // this.loadPendingRequests();
    // this.loadStatusRows();

    // or is fetching and deriving both views preferred?
    this.loading.set(true);
    this.errorMessage.set(null);
    this.statusLoading.set(true);
    this.statusErrorMessage.set(null);

    forkJoin({
      users: this.userService.getUsers(),
      pendingRequests: this.userService.getApiKeyRequests(GenApiKeyRequestStatus.PENDING),
      allRequests: this.userService.getApiKeyRequests(),
    }).subscribe({
      next: ({ users, pendingRequests, allRequests }) => {
        this.requestRows.set(this.mapRows(users, pendingRequests));
        this.statusRows.set(this.mapStatusRows(users, allRequests));
        this.loading.set(false);
        this.statusLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Unable to load API key requests. Please refresh and try again.');
        this.statusErrorMessage.set('Unable to load API key status records. Please refresh and try again.');
        this.loading.set(false);
        this.statusLoading.set(false);
      },
    });
  }

  private loadPendingRequests(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    forkJoin({
      users: this.userService.getUsers(),
      requests: this.userService.getApiKeyRequests(GenApiKeyRequestStatus.PENDING),
    }).subscribe({
      next: ({ users, requests }) => {
        this.requestRows.set(this.mapRows(users, requests));
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set('Unable to load API key requests. Please refresh and try again.');
        this.loading.set(false);
      }
    });
  }

  private loadStatusRows(): void {
    this.statusLoading.set(true);
    this.statusErrorMessage.set(null);

    forkJoin({
      users: this.userService.getUsers(),
      requests: this.userService.getApiKeyRequests(),
    }).subscribe({
      next: ({ users, requests }) => {
        this.statusRows.set(this.mapStatusRows(users, requests));
        this.statusLoading.set(false);
      },
      error: () => {
        this.statusErrorMessage.set('Unable to load API key status records. Please refresh and try again.');
        this.statusLoading.set(false);
      }
    });
  }

  private mapRows(users: User[], requests: ApiKeyRequest[]): ApiKeyUserRequestRow[] {
    const userById = new Map<number, User>(users.map((user) => [user.id, user]));
    const requestGroups = new Map<number, ApiKeyRequestDetail[]>();

    for (const request of requests) {
      if (request.userId == null || request.id == null) {
        continue;
      }

      const user = userById.get(request.userId);
      if (!user) {
        continue;
      }

      const requestedAt = request.createdAt?.toDate ? request.createdAt.toDate() : null;
      const requestDetail: ApiKeyRequestDetail = {
        requestId: request.id,
        requestReason: request.reason,
        requestedKeysCount: 1,
        organization: user.organization,
        requestedAt,
        supportedUsersCount: request.supportedSystems?.length ?? 0,
      };

      const userRequests = requestGroups.get(user.id);
      if (userRequests) {
        userRequests.push(requestDetail);
      } else {
        requestGroups.set(user.id, [requestDetail]);
      }
    }

    return Array.from(requestGroups.entries())
      .map(([userId, userRequests]) => {
      const user = userById.get(userId);
      if (!user) {
        return null;
      }

      return {
        userId,
        userName: this.getUserDisplayName(user),
        organization: user.organization || 'N/A',
        requestedKeyCount: userRequests.length,
        requests: userRequests.sort((a, b) => {
          const aTime = a.requestedAt?.getTime() ?? 0;
          const bTime = b.requestedAt?.getTime() ?? 0;
          return bTime - aTime;
        }),
      };
    })
      .filter((row): row is ApiKeyUserRequestRow => row !== null)
      .sort((a, b) => b.requestedKeyCount - a.requestedKeyCount || a.userName.localeCompare(b.userName));
  }

  private mapStatusRows(users: User[], requests: ApiKeyRequest[]): ApiKeyStatusRow[] {
    const deniedRequestsByUserId = new Map<number, ApiKeyRequest[]>();

    for (const request of requests) {
      if (request.userId == null || request.status !== GenApiKeyRequestStatus.DENIED) {
        continue;
      }

      const userRequests = deniedRequestsByUserId.get(request.userId);
      if (userRequests) {
        userRequests.push(request);
      } else {
        deniedRequestsByUserId.set(request.userId, [request]);
      }
    }

    return users
      .map((user) => {
        const details: ApiKeyStatusDetail[] = [];

        for (const apiKey of user.apiKeys ?? []) {
          if (apiKey.id == null) {
            continue;
          }

          const expirationDate = apiKey.expiresAt?.toDate ? apiKey.expiresAt.toDate() : null;
          const isExpired = expirationDate ? expirationDate.getTime() <= Date.now() : false;
          const status = !apiKey.enabled ? 'Denied' : isExpired ? 'Expired' : 'Active';
          const reason = !apiKey.enabled
            ? 'API key was disabled after being issued.'
            : isExpired
              ? 'API key expired because the expiration date has passed.'
              : null;

          details.push({
            id: `key-${apiKey.id}`,
            status,
            expirationDate,
            supportedSystems: apiKey.supportedSystems ?? [],
            reason,
          });
        }

        for (const request of deniedRequestsByUserId.get(user.id) ?? []) {
          if (request.id == null) {
            continue;
          }

          details.push({
            id: `request-${request.id}`,
            status: 'Denied',
            expirationDate: null,
            supportedSystems: request.supportedSystems ?? [],
            reason: request.deniedReason || 'API key request was denied.',
          });
        }

        return {
          userId: user.id,
          userName: this.getUserDisplayName(user),
          apiKeyCount: details.length,
          details,
        };
      })
      .filter((row) => row.details.length > 0)
      .sort((a, b) => b.apiKeyCount - a.apiKeyCount || a.userName.localeCompare(b.userName));
  }

  private getUserDisplayName(user: User): string {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return fullName || user.username || `User #${user.id}`;
  }

  private isProcessing(requestId: number): boolean {
    return this.processingRequestIds().has(requestId);
  }

  private addProcessingRequestId(requestId: number): void {
    this.processingRequestIds.update((ids) => new Set(ids).add(requestId));
  }

  private removeProcessingRequestId(requestId: number): void {
    this.processingRequestIds.update((ids) => {
      const next = new Set(ids);
      next.delete(requestId);
      return next;
    });
  }

  private executeDecision(
    requestId: number,
    request$: Observable<unknown>,
    failureTitle: string,
  ): void {
    if (this.isProcessing(requestId)) {
      return;
    }

    this.addProcessingRequestId(requestId);

    request$
      .pipe(finalize(() => this.removeProcessingRequestId(requestId)))
      .subscribe({
        next: () => this.reloadTables(),
        error: (error: unknown) => this.openDecisionErrorDialog(error, failureTitle),
      });
  }
}
